import { DATE_FORMAT } from '@/constants/common.constant'
import { AxiomReportTypeFullNames } from '@/enums/axiom-report.enum'
import { ActionMode } from '@/enums/scenario-builder.enum'
import { useAppSelector } from '@/hooks/use-app-selector'
import { TReportTemplate } from '@/interfaces/scenarioBuilder.interface'
import { getReportTemplates } from '@/services/scenarioBuilder.service'
import {
  setActionMode,
  setSelectedReportTemplate,
} from '@/states/scenarioBuilder.slice'
import {
  ColDef,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  RowClickedEvent,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { Box } from '@mui/material'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { LoadingCard } from '../Base/LoadingCard/LoadingCard'
import { ReportTemplateGridMenu } from './ReportTemplateGridMenu'

export const ReportTemplateGrid: React.FC = () => {
  const gridRef = useRef<AgGridReact>(null)
  const dispatch = useDispatch()
  const [reportTemplates, setReportTemplates] = useState<TReportTemplate[]>([])
  const [reportTemplatesLoading, setReportTemplatesLoading] =
    useState<boolean>(false)
  const isReloadingReportTemplateGrid = useAppSelector(
    (s) => s.scenarioBuilder.isReloadingReportTemplateGrid,
  )
  useEffect(() => {
    const fetchReportTemplates = async () => {
      setReportTemplatesLoading(true)
      try {
        const reportTemplatesData = await getReportTemplates()
        if (reportTemplatesData?.length) {
          setReportTemplates(reportTemplatesData)
        }
      } catch (err) {
        console.warn(err)
      } finally {
        setReportTemplatesLoading(false)
      }
    }
    fetchReportTemplates()
  }, [isReloadingReportTemplateGrid])

  const gridOptions: GridOptions = {
    rowSelection: 'single',
    suppressMenuHide: true,
    rowStyle: { cursor: 'pointer' },
    defaultColDef: {
      filter: 'agTextColumnFilter',
      floatingFilter: false,
      suppressHeaderMenuButton: false,
      suppressHeaderFilterButton: false,
      menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
      filterParams: {
        filterOptions: [
          'equals',
          'contains',
          'doesNotContain',
          'doesNotEqual',
          'beginsWith',
          'endsWith',
          'blank',
          'notBlank',
        ],
        suppressAndOrCondition: true,
      },
    },
    columnDefs: [
      {
        cellRenderer: (params: ICellRendererParams) => (
          <ReportTemplateGridMenu {...params} />
        ),
        suppressHeaderMenuButton: true,
        width: 50,
      },
      {
        headerName: 'ID',
        field: 'id',
        hide: true,
      },
      {
        headerName: 'Name',
        field: 'name',
      },
      {
        headerName: 'Report Type',
        field: 'reportType',
        valueFormatter: (params: ValueFormatterParams) => {
          const reportType = params.value as keyof typeof AxiomReportTypeFullNames
          return AxiomReportTypeFullNames[reportType] || params.value
        },
      },
      {
        headerName: 'User Name',
        field: 'username',
      },
      {
        headerName: 'Hedging Param',
        field: 'hedgingParam',
      },
      {
        headerName: 'Hedged Value',
        field: 'hedgedValue',
      },
      {
        headerName: 'Date',
        field: 'lastSavedTime',
        valueFormatter: (params: ValueFormatterParams) => {
          const v = params.value ?? params?.data?.lastSavedTime
          return v ? dayjs(v).format(DATE_FORMAT.TABLE_CHART) : ''
        },
      },
    ] as ColDef[],

    onRowClicked: (params: RowClickedEvent) => {
      const nodes = params.api.getSelectedNodes()
      dispatch(setActionMode(ActionMode.UPDATE))
      dispatch(setSelectedReportTemplate(nodes[0].data))
    },
  } as GridOptions

  const noRowsOverlay = () => {
    return 'No report templates available.'
  }
  const sizeToFit = useCallback(() => {
    const api = gridRef.current?.api
    if (api) {
      api.sizeColumnsToFit()
    }
  }, [])

  useEffect(() => {
    // Event listener for window resize
    window.addEventListener('resize', sizeToFit)

    // Cleanup
    return () => {
      window.removeEventListener('resize', sizeToFit)
    }
  }, [sizeToFit])
  const onGridReady = (params: GridReadyEvent) => {
    params.api.sizeColumnsToFit()
  }

  return (
    <Box 
      className='ag-theme-quartz' 
      data-testid='report-template-grid'
      sx={{ height: '400px', width: '100%' }}
    >
      {reportTemplatesLoading ? (
        <LoadingCard />
      ) : (
        <AgGridReact
          ref={gridRef}
          gridOptions={gridOptions}
          onGridReady={onGridReady}
          rowData={reportTemplates}
          data-cy='ag-grid'
          noRowsOverlayComponent={noRowsOverlay}
        />
      )}
    </Box>
  )
}
